import { graphqlOperation } from "@aws-amplify/api";
import { DataStore } from "@aws-amplify/datastore";
import useAPI from "@toothfairy/shared-api/useApi";
import AppQuantumEditorUtils from "@toothfairy/shared-ui/AppQuantumEditorUtils";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import axios from "axios";
import dayjs from "dayjs";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  isConnectedToCloud,
  isElectronRunning,
  isOllamaRunning,
  oLlamaModelLoaded,
} from "../../../App";
import envConfig from "../../../envConfig";
import { subscribeGraphQL } from "../../API";
import chatsHelper from "../../API/Chats/chatsHelper";
import Chatter from "../../API/Chatter";
import Files from "../../API/Files";
import hostingsHelper from "../../API/Hostings/hostingsHelper";
import Media from "../../API/Media";
import messagesHelper from "../../API/Messages/messagesHelper";
import { onUpdateChatPlanner } from "../../graphql/custom";
import {
  onCreateChatMessageByChatIDEvent,
  onUpdateChatMessageByChatIDEvent,
} from "../../graphql/subscriptions";
import useOllamaModel from "../../Hooks/useOllamaModels";
import useRecording from "../../Hooks/useRecording";
import { useTextToSpeech } from "../../Hooks/useTextToSpeech";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { Chat } from "../../models";
import { useHistory, useParams } from "../../Router";
import { isMobileScreen } from "../MainViewContainer";
import ChatsScreenView from "./ChatsScreenView";

const Chats = () => {
  const MAX_MESSAGES_LOADED = 20;
  const { chatId } = useParams();
  const history = useHistory();
  const { mode } = AppTheme?.useTheme();
  const cursor = mode === "light" ? "\u26AB" : "\u26AA";
  const [lastUpdateMessageTime, setLastUpdateMessageTime] = useState(null);
  const lastUpdateMessageTimeRef = useRef({});
  const {
    getActiveWorkspace,
    getWorkspaceEntities,
    generateAdaptedTopics,
    loadChats,
    getAgent,
    getChat,
    isChatHistoryLoaded,
    setIsChatHistoryLoaded,
    getPlanConfig,
  } = useWorkspaces();
  const {
    updateText: updateTextForSpeech,
    currentText,
    processedText,
    isSpeechGettingProcessed,
    stopAudio,
    isPlaying,
    setIsPlaying,
    listenToAudio,
  } = useTextToSpeech();
  const { generateSystemMessage } = useOllamaModel();
  const [lastSubscribedChatId, setLastSubscribedChatId] = useState(null);
  const isSendingEventRef = useRef(false);
  const [agentHosting, setAgentHosting] = useState(null);
  const [errorWithSubscription, setErrorWithSubscription] = useState(false);
  const [subscriptionObj, setSubscriptionObject] = useState(null);
  const [chatSubscriptionObj, setChatSubscriptionObject] = useState(null);
  const [chatMetadata, setChatMetadata] = useState(null);
  const [allowStop, setAllowStop] = useState(false);
  const [selectedImageKey, setSelectedImageKey] = useState(null);
  const [selectedImageRawKey, setSelectedImageRawKey] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const lastMessageCreatedRef = useRef(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileKey, setFileKey] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioKey, setAudioKey] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoKey, setVideoKey] = useState(null);
  const [fileUrls, setFileUrls] = useState(null);
  const [fileKeys, setFileKeys] = useState(null);
  const chatIdRef = useRef(null);
  const [isSubscriptionRequired, setIsSubscriptionRequired] = useState(false);
  const __isAIStreamingForSubscription = useRef(false);
  const streamingMessageIDRef = useRef(null);
  const [audiosText, setAudiosText] = useState("");
  const [videosText, setVideosText] = useState("");
  const [planningExecution, setPlanningExecution] = useState(null);
  const [audioPlayBackMessage, setAudioPlayBackMessage] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [totalSteps, setTotalSteps] = useState(0);
  const [streamingMessageID, setStreamingMessageID] = useState(null);
  const [loadedMessages, setLoadedMessages] = useState([MAX_MESSAGES_LOADED]);
  const [stateMessages, setStateMessages] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [planArtifacts, setPlanArtifacts] = useState([]);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [showWidgetChatsHistory, setShowWidgetChatsHistory] = useState(false);
  const [chatObject, setChatObject] = useState(null);
  const [primaryAgent, setPrimaryAgent] = useState(null);
  const primaryAgentModeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedChatId, setLoadedChatId] = useState(null);
  const [_coreTreeViewData, setCoreTreeViewData] = useState(null);
  const [inputText, setInputText] = useState("");
  const [showModal, setShowModal] = React.useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [canInteractWithChat, setCanInteractWithChat] = useState(true);
  const [creationOfNewChatInProgress, setCreationOfNewChatInProgress] =
    React.useState(false);
  const [deletionInProgress, setDeletionInProgress] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(null);
  const [treeViewActionContext, setTreeViewActionContext] = useState("");
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const { user } = AppUser.useUser();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const lastChangedChatRef = useRef(null);
  const loadingMessageIdRef = useRef(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [imageKey, setImageKey] = useState(null);
  const chats = state?.chats?.filter((u) => {
    if (showWidgetChatsHistory) return u?.visibility === "widget";
    else return u?.visibility !== "widget";
  });
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef(messages);
  const localStreamingController = useRef(null);
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const isAIStreamingRef = useRef(false);
  const setChats = (chats) => {
    dispatch("SET_CHATS", chats);
  };
  const topics = getWorkspaceEntities("topic")?.map((topic) => {
    topic.name = topic?.label;
    topic.category = topic.type;
    return topic;
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeToDelete, setSelectedNodeToDelete] = useState(null);
  useEffect(() => {
    setChatObject(null);
    setImageUrl(null);
    setImageKey(null);
    setAudioUrl(null);
    setFileKey(null);
    setFileUrl(null);
    setFileKeys(null);
    setFileUrls(null);
    setAudioKey(null);
    setVideoUrl(null);
    setVideoKey(null);
    setSelectedImageKey(null);
    setIsAIStreaming(false);
    setLastUpdateMessageTime(null);
    lastChangedChatRef.current = null;
    loadingMessageIdRef.current = null;
    setActiveStep(null);
    isSendingEventRef.current = false;
    chatIdRef.current = chatId;
  }, [chatId]);
  useEffect(() => {
    primaryAgentModeRef.current = primaryAgent?.mode;
  }, [primaryAgent?.mode]);
  const { transcript, startRecording, stopRecording } = useRecording({
    onForcedStop: () => {},
  });
  const {
    loading: audioProcessingInProgress,
    apiRequest: audioProcessingRequest,
    wasInvoked: audioProcessingInvoked,
    data: audioProcessingResult,
  } = useAPI(Media.processAudio, null, () => {});
  const {
    loading: videoProcessingInProgress,
    apiRequest: videoProcessingRequest,
    wasInvoked: videoProcessingInvoked,
    data: videoProcessingResult,
  } = useAPI(Media.processVideo, null, () => {});

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  useEffect(() => {
    const _id = state?.chats?.find((u) => u?.id === chatId)?.activeMessageForAI;
    loadingMessageIdRef.current = _id;
    setStreamingMessageID(_id);
    setDocumentId(state?.chats?.find((u) => u?.id === chatId)?.documentId);
  }, [JSON.stringify(state?.chats?.map((u) => u?.activeMessageForAI)), chatId]);
  const onImageUploaded = (url) => {
    setImageUrl(url);
    setImageKey(url);
  };

  const extractFilesInfoFromArtifacts = (steps_artifacts) => {
    const result = [];

    for (const step of steps_artifacts) {
      const { files, images, audios, videos } = step;

      if (files && files.files_keys) {
        for (const fileKey of files.files_keys) {
          result.push({
            file_key: fileKey,
            description_from_step_definition:
              files.description_from_step_definition,
            file_type: "file",
          });
        }
      }

      if (images && images.files_keys) {
        for (const fileKey of images.files_keys) {
          result.push({
            file_key: fileKey,
            description_from_step_definition:
              images.description_from_step_definition,
            file_type: "image",
          });
        }
      }

      if (audios && audios.files_keys) {
        for (const fileKey of audios.files_keys) {
          result.push({
            file_key: fileKey,
            description_from_step_definition:
              audios.description_from_step_definition,
            file_type: "audio",
          });
        }
      }

      if (videos && videos.files_keys) {
        for (const fileKey of videos.files_keys) {
          result.push({
            file_key: fileKey,
            description_from_step_definition:
              videos.description_from_step_definition,
            file_type: "video",
          });
        }
      }
    }

    return result;
  };
  useEffect(() => {
    if (chatObject?.metadata) {
      let _metadata = {};
      if (typeof chatObject?.metadata === "string")
        _metadata = JSON.parse(chatObject?.metadata);
      else {
        _metadata = chatObject?.metadata;
      }
      let _steps_artifacts = [];
      // check if string and if so convert to JSOn
      if (typeof _metadata?.steps_artifacts === "string") {
        _steps_artifacts = JSON.parse(_metadata?.steps_artifacts);
      } else {
        _steps_artifacts = _metadata?.steps_artifacts;
      }
      _adapted_files = extractFilesInfoFromArtifacts(_steps_artifacts || []);
      setPlanArtifacts(_adapted_files);
    } else {
      setPlanArtifacts([]);
    }
  }, [JSON.stringify(chatObject)]);
  useEffect(() => {
    isAIStreamingRef.current = isAIStreaming;
  }, [isAIStreaming]);
  const checkIfOllamaIsRunning = async () => {
    try {
      const response = await axios.get(`${agentHosting?.endpoint}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response?.status == 200) {
        isOllamaRunning(true);
        oLlamaModelLoaded(agentHosting?.modelName);
      } else {
        throw new Error("Ollama could not be loaded");
      }
    } catch (error) {
      console.log("Error", error);
      AppToasty.show(
        isElectronRunning()
          ? `Ollama not available on web, please use the desktop client`
          : `Error loading ${agentHosting?.modelName} using Ollama at ${agentHosting?.endpoint}`,
        {
          placement: "bottom",
          type: "danger",
        }
      );
    }
  };
  useEffect(() => {
    if (agentHosting?.type == "ollama") {
      checkIfOllamaIsRunning();
    }
  }, [agentHosting?.type]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [JSON.stringify(messages)]);
  const onFileUploaded = (url) => {
    setFileUrl(url);
    setFileKey(url);
  };
  const onFilesUploaded = (urls, presignedUrls, indexForRemoval = null) => {
    if (indexForRemoval !== null && fileUrls?.length > 0) {
      setFileUrls((prev) => {
        const newArray = [...prev];
        newArray.splice(indexForRemoval, 1);
        return newArray;
      });
      setFileKeys((prev) => {
        const newArray = [...prev];
        newArray.splice(indexForRemoval, 1);
        return newArray;
      });
      return;
    } else if (fileUrls?.length > 0) {
      setFileUrls((prev) => [...prev, ...presignedUrls]);
      setFileKeys((prev) => [...prev, ...urls]);
    } else {
      setFileUrls(presignedUrls);
      setFileKeys(urls);
    }
  };

  const getHostingsDetails = async (primaryAgent) => {
    const hostingsData = await hostingsHelper.getHostings(
      primaryAgent?.workspaceID,
      null
    );
    let hostings = hostingsData?.data?.HostingsByWorkspace?.items;
    hostings =
      hostings
        ?.map((u) => {
          if (u?._deleted !== true)
            return {
              ...u,
            };
        })
        ?.filter((u) => u != null) || [];
    const _localHosting = hostings?.find(
      (u) => u?.name == primaryAgent?.llmProvider
    );
    setAgentHosting(_localHosting);
  };
  useEffect(() => {
    if (
      primaryAgent?.llmProvider != null &&
      primaryAgent?.llmProvider != "tf"
    ) {
      getHostingsDetails(primaryAgent);
    } else {
      setAgentHosting(null);
    }
  }, [primaryAgent?.id, primaryAgent?.llmProvider]);
  const onAudioUploaded = async (url) => {
    try {
      setAudioKey(url);
      setAudioUrl(url);
      const result = await audioProcessingRequest({
        token: getActiveWorkspace()?.workspaceToken,
        data: {
          audio_file: url,
          workspaceid: getActiveWorkspace()?.id,
        },
      });

      setAudiosText(result?.contents?.text);
      // AppToasty.show("Audio processed", {
      //   placement: "bottom",
      //   type: "success",
      // });
    } catch (error) {
      setAudiosText("");
      // AppToasty.show("Error processing audio", {
      //   placement: "bottom",
      //   type: "danger",
      // });
    }
  };
  const onImageSelected = (url, key) => {
    setSelectedImageKey(url);
    setSelectedImageRawKey(key);
  };
  const onVideoUploaded = async (url) => {
    try {
      setVideoKey(url);
      setVideoUrl(url);
      const result = await videoProcessingRequest({
        token: getActiveWorkspace()?.workspaceToken,
        data: {
          text_prompt: "Summarize the video in no more than 5 sentences",
          video_file: url,
          workspaceid: getActiveWorkspace()?.id,
        },
      });
      setVideosText(result?.contents);
      // AppToasty.show("Video processed", {
      //   placement: "bottom",
      //   type: "success",
      // });
    } catch (error) {
      setVideosText("");
      // AppToasty.show("Error processing video", {
      //   placement: "bottom",
      //   type: "danger",
      // });
    }
  };
  const generateUniqueMessages = (updatedMessages) => {
    const uniqueMessages = new Map();
    updatedMessages.forEach((message) => {
      const existingMessage = uniqueMessages.get(message._id);
      if (!existingMessage) {
        uniqueMessages.set(message._id, message);
      } else {
        // If an existing message with the same ID is found
        // Keep the message with the greater _lastChangedAt value
        if (message._lastChangedAt > existingMessage._lastChangedAt) {
          uniqueMessages.set(message._id, message);
        }
      }
    });
    updatedMessages = Array.from(uniqueMessages.values());
    return updatedMessages;
  };
  const handleCopy = () => {
    AppToasty.show("🎉 Copied", {
      placement: "bottom",
      type: "success",
    });
  };
  const handleFeedback = async (
    id,
    humanFeedback,
    humanFeedbackType,
    humanFeedbackComment,
    humanFeedbackSubType,
    humanFeedbackForInstruction = false
  ) => {
    const payload = [
      {
        property: "humanFeedback",
        value: humanFeedback,
      },
      {
        property: "humanFeedbackProvidedBy",
        value: user?.id,
      },
      {
        property: "adminChecked",
        value: false,
      },
    ];
    if (humanFeedbackType) {
      payload.push({
        property: "humanFeedbackType",
        value: humanFeedbackType,
      });
    }
    if (humanFeedbackSubType) {
      payload.push({
        property: "humanFeedbackSubType",
        value: humanFeedbackSubType,
      });
    }
    if (humanFeedbackComment) {
      payload.push({
        property: "humanFeedbackComment",
        value: humanFeedbackComment,
      });
    }

    payload.push({
      property: "humanFeedbackForInstruction",
      value: humanFeedbackForInstruction,
    });

    try {
      const result = await messagesHelper.updateChatMessage(id, payload);
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        const newMessageIndex = updatedMessages.map((u) => u._id).indexOf(id);
        if (newMessageIndex !== -1) {
          updatedMessages[newMessageIndex] = {
            ...updatedMessages[newMessageIndex],
            humanFeedback: humanFeedback,
            humanFeedbackType: humanFeedbackType,
            humanFeedbackComment: humanFeedbackComment,
            humanFeedbackSubType: humanFeedbackSubType,
            humanFeedbackForInstruction: humanFeedbackForInstruction,
          };
        }
        return [...updatedMessages];
      });
      AppToasty.show("Feedback saved", {
        placement: "bottom",
        type: "success",
      });
    } catch (error) {
      console.log(error);
      AppToasty.show("Error saving feedback", {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const handleOnRecorderClick = (v) => {
    try {
      if (v) {
        startRecording();
        // show the toast message
        AppToasty.show(t("recordingStarted"), {
          placement: "bottom",
          type: "success",
        });
      } else {
        stopRecording();
        AppToasty.show(t("recordingStopped"), {
          placement: "bottom",
          type: "success",
        });
      }
    } catch (error) {
      AppToasty.show(t("recordingError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  function removeSpecificUnicodeAtEnd(text) {
    if (text) {
      const unicodeRegex = /(\u26AB|\u26AA)$/g;
      return text.replace(unicodeRegex, "");
    } else {
      return "";
    }
  }
  const onCancelGeneration = async () => {
    if (localStreamingController?.current) {
      localStreamingController.current.abort();
      localStreamingController.current = null;
      setIsAIStreaming(false);
      setAllowStop(false);
    } else {
      subscriptionObj?.unsubscribe();
      setIsSubscriptionRequired(true);
      setIsAIStreaming(false);
      setLastUpdateMessageTime(null);
      const _payload = [
        {
          property: "isAIReplying",
          value: false,
        },
        {
          property: "activeMessageForAI",
          value: null,
        },
      ];
      if (primaryAgent?.mode == "planner" || primaryAgent?.mode == "computer")
        _payload.push({
          property: "forcePlannerStop",
          value: true,
        });

      await chatsHelper.updateChat(chatId, _payload);
      const result = await messagesHelper.updateChatMessage(
        streamingMessageID,
        [
          {
            property: "status",
            value: "cancelled",
          },
        ]
      );
      setChatObject({
        ...chatObject,
        isAIReplying: false,
        activeMessageForAI: null,
        forcePlannerStop:
          primaryAgent?.mode == "planner" || primaryAgent?.mode == "computer"
            ? true
            : false,
      });
      if (primaryAgent?.mode == "planner") {
        AppToasty.show(t("planExecutionStopRequested"), {
          placement: "bottom",
          type: "success",
        });
      }
    }
  };
  const handleClickOnWidgetChatsHistory = () => {
    if (getPlanConfig()?.isWidgetChatsHistoryEnabled) {
      setSelectedNode(null);
      history.push(`/workspaces/${getActiveWorkspace()?.id}/chats`);
      loadChats(!showWidgetChatsHistory, true);
      setShowWidgetChatsHistory(!showWidgetChatsHistory);
    } else {
      AppToasty.show(t("widgetChatsHistoryNotEnabled"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  useEffect(() => {
    let subscriptionObjCreate;
    const setupSubscription = async () => {
      if (chatId) {
        if (isConnectedToCloud()) setErrorWithSubscription(false);
        setAllowStop(false);
        subscriptionObjCreate = await subscribeGraphQL(
          graphqlOperation(onCreateChatMessageByChatIDEvent, {
            chatID: chatId,
          }),
          {
            next: async (data) => {
              const mainMessageObj =
                data?.value?.data?.onCreateChatMessageByChatIDEvent;
              if (mainMessageObj?.chatID != chatId) return;
              lastMessageCreatedRef.current = mainMessageObj?.id;
              const message = mainMessageObj?.text;
              const newMessage = {
                ...mainMessageObj,
                _id: mainMessageObj.id,
                createdAt: mainMessageObj.creationTime,
                _lastChangedAt: mainMessageObj?._lastChangedAt,
                user: {
                  id: null,
                  name: "assistant",
                  agentName:
                    primaryAgent?.id != mainMessageObj?.agentID
                      ? getAgent(mainMessageObj?.agentID)?.label
                      : primaryAgent?.label,
                },
                text: `${message}`,
                loading: false,
                humanFeedback: mainMessageObj?.humanFeedback,
                humanFeedbackType: mainMessageObj?.humanFeedbackType,
                humanFeedbackSubType: mainMessageObj?.humanFeedbackSubType,
                humanFeedbackComment: mainMessageObj?.humanFeedbackComment,
                metadata:
                  typeof mainMessageObj.metadata === "string"
                    ? JSON.parse(mainMessageObj.metadata)
                    : mainMessageObj.metadata,
                callbackMetadata:
                  typeof mainMessageObj.callbackMetadata === "string"
                    ? JSON.parse(mainMessageObj.callbackMetadata)
                    : mainMessageObj.callbackMetadata,
                agentName: getAgent(mainMessageObj?.agentID)?.label,
              };
              setTimeout(() => {
                setMessages((prevMessages) => {
                  let updatedMessages = [...prevMessages];
                  if (
                    prevMessages?.find((u) => u?._id == newMessage?._id) == null
                  )
                    updatedMessages.push(newMessage);
                  // sort messages by createdAt
                  // const uniqueMessages = new Map();
                  // updatedMessages.forEach((message) => {
                  //   const existingMessage = uniqueMessages.get(message._id);
                  //   if (
                  //     !existingMessage ||
                  //     new Date(existingMessage._lastChangedAt) <
                  //       new Date(message._lastChangedAt)
                  //   ) {
                  //     uniqueMessages.set(message._id, message);
                  //   }
                  // });
                  // updatedMessages = Array.from(uniqueMessages.values());
                  updatedMessages = updatedMessages.sort((a, b) => {
                    return new Date(a.createdAt) - new Date(b.createdAt);
                  });
                  return [...updatedMessages];
                });
              }, 1000);
            },
            error: (error) => {
              console.log(`Error with subscription: ${chatId}`, error);
            },
            close: () => {
              setIsSocketConnected(false);
            },
            connect: () => {
              setIsSocketConnected(true);
            },
          }
        );
        subscribeToChatMessages(chatId);
      }
    };
    setupSubscription();
    return () => {
      setIsSocketConnected(false);
      if (subscriptionObjCreate && subscriptionObjCreate?.unsubscribe)
        subscriptionObjCreate?.unsubscribe();
    };
  }, [chatId, isSendingEventRef?.current]);
  const subscribeToPlanner = (chatId, streamingMessageID) => {
    const setupSubscription = async () => {
      const sub = await subscribeGraphQL(
        graphqlOperation(onUpdateChatPlanner, {
          id: chatId,
        }),
        {
          next: async (data) => {
            let chatObj = data?.value?.data?.onUpdateChat;
            if (chatObj?.id != chatId) return;
            if (
              !lastChangedChatRef.current ||
              chatObj?._lastChangedAt > lastChangedChatRef.current
            )
              lastChangedChatRef.current = chatObj?._lastChangedAt;
            else if (chatObj?.id === chatId) {
              if (primaryAgent?.mode == "planner")
                setIsAIStreaming(
                  chatObj?.isAIReplying != false &&
                    chatObj?.metadata?.planning_status !=
                      "plan_execution_completed" &&
                    chatObj?.metadata?.planning_status !=
                      "plan_execution_failed"
                );
              else {
                setIsAIStreaming(chatObj?.isAIReplying == true);
              }
              return;
            } else return;
            if (chatObj?.metadata && typeof chatObj?.metadata === "string")
              chatObj.metadata = JSON.parse(chatObj.metadata);
            try {
              const objectIndex = state?.chats?.findIndex(
                (log) => log.id === chatObj?.id
              );
              if (objectIndex >= 0 && state?.chats?.length > 1) {
                const updatedProperty = [...state["chats"]];
                updatedProperty[objectIndex] = chatObj;
                dispatch("SET_CHATS", updatedProperty);
              }
            } catch (error) {
              console.log(
                "Failed to update the chats using subscriptions",
                error
              );
            }

            // update chat title if it has changed
            if (chatObj?.id === chatId) {
              if (
                chatObj?.name !== chatObject?.name ||
                JSON.stringify(chatObj?.metadata) !==
                  JSON.stringify(chatObject?.metadata)
              ) {
                setChatObject(chatObj);
              }
              if (
                chatObj?.primaryRole &&
                chatObj?.primaryRole !== chatObject?.primaryRole
              ) {
                if (getAgent(chatObj?.primaryRole))
                  setPrimaryAgent(getAgent(chatObj?.primaryRole));
              }
              if (chatObj?.metadata) {
                // if metadata is a sting, parse it
                if (typeof chatObj?.metadata === "string") {
                  chatObj.metadata = JSON.parse(chatObj?.metadata);
                }
                setPlanningExecution(chatObj?.metadata?.planning_status);
                //  possible planning_status values: planning_not_available, planning_requires_approval, planning_approved, plan_execution_in_progress, plan_execution_completed, plan_execution_failed,
                if (chatObj?.isAIReplying != false) {
                  if (typeof chatObj?.metadata?.plan === "string")
                    chatObj.metadata.plan = JSON.parse(chatObj?.metadata?.plan);
                  setIsAIStreaming(true);
                  setActiveStep(chatObj?.metadata?.active_step);
                  setTotalSteps(chatObj?.metadata?.plan?.length || 0);
                }
              }
              if (primaryAgent?.mode == "planner")
                setIsAIStreaming(
                  chatObj?.isAIReplying != false &&
                    chatObj?.metadata?.planning_status !=
                      "plan_execution_completed" &&
                    chatObj?.metadata?.planning_status !=
                      "plan_execution_failed"
                );
              else {
                setIsAIStreaming(chatObj?.isAIReplying == true);
              }
            }
          },
          error: (error) => {
            console.log(`Error with chat subscription: ${chatId}`, error);
          },
        }
      );
      return sub;
    };
    return setupSubscription();
  };
  useEffect(() => {
    streamingMessageIDRef.current = streamingMessageID;
  }, [streamingMessageID]);
  const subscribeToChatMessages = (
    chatId,
    streamingMessageID,
    primaryAgent
  ) => {
    const messageQueue = [];
    let lastProcessedTimestamp = {};
    let isProcessingQueue = false;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    const setupSubscription = async () => {
      try {
        const sub = await subscribeGraphQL(
          graphqlOperation(onUpdateChatMessageByChatIDEvent, {
            chatID: chatId,
          }),
          {
            next: async (data) => {
              try {
                const mainMessageObj =
                  data?.value?.data?.onUpdateChatMessageByChatIDEvent;
                if (!mainMessageObj || mainMessageObj?.chatID !== chatId)
                  return;

                const timestamp = mainMessageObj._lastChangedAt;
                const messageId = mainMessageObj.id;

                if (!timestamp || !messageId) return;

                // Deduplicate messages by checking both ID and timestamp
                const existingMessageIndex = messageQueue.findIndex(
                  (item) => item.messageId === messageId
                );

                const lastTimestamp = lastProcessedTimestamp[messageId] || 0;
                if (existingMessageIndex === -1 || timestamp > lastTimestamp) {
                  // Remove any existing message with same ID
                  if (existingMessageIndex !== -1) {
                    messageQueue.splice(existingMessageIndex, 1);
                  }

                  messageQueue.push({ timestamp, mainMessageObj, messageId });
                  messageQueue.sort((a, b) => a.timestamp - b.timestamp);

                  // Reset retry count on successful message
                  retryCount = 0;
                }

                processQueue();
              } catch (error) {
                console.error("Error processing subscription data:", error);
              }
            },
            error: (error) => {
              console.error(
                `Error with subscription for chat ${chatId}:`,
                error
              );
              setErrorWithSubscription(true);

              // Implement exponential backoff retry
              if (retryCount < MAX_RETRIES) {
                retryCount++;
                const delay = RETRY_DELAY * Math.pow(2, retryCount - 1);
                setTimeout(() => {
                  console.log(
                    `Retrying subscription (attempt ${retryCount}/${MAX_RETRIES})`
                  );
                  setupSubscription();
                }, delay);
              } else {
                console.error("Max retries reached for subscription");
                if (lastSubscribedChatId < 10)
                  setLastSubscribedChatId((prev) => prev + 1);
              }
            },
            close: () => {
              console.log("Subscription closed");
              setIsSocketConnected(false);
            },
            connect: () => {
              console.log("Subscription connected");
              setIsSocketConnected(true);
              retryCount = 0;
            },
          }
        );
        return sub;
      } catch (error) {
        console.error("Failed to setup subscription:", error);
        setErrorWithSubscription(true);
        throw error;
      }
    };

    const processQueue = () => {
      if (isProcessingQueue || messageQueue.length === 0) return;

      isProcessingQueue = true;

      const processNextMessage = async () => {
        if (messageQueue.length === 0) {
          isProcessingQueue = false;
          return;
        }

        const { timestamp, mainMessageObj, messageId } = messageQueue.shift();

        // Only process if this is newer than what we've already processed
        const lastTimestamp = lastProcessedTimestamp[messageId] || 0;
        if (timestamp > lastTimestamp) {
          lastProcessedTimestamp[messageId] = timestamp;

          try {
            await processMessages(mainMessageObj);
          } catch (error) {
            console.error("Error processing message:", error);
          }
        }

        // Use requestAnimationFrame for better performance
        requestAnimationFrame(processNextMessage);
      };

      processNextMessage();
    };

    const processMessages = async (mainMessageObj) => {
      const message = mainMessageObj?.text;
      const _streamingMessageID = streamingMessageIDRef?.current;
      const messageId = mainMessageObj?.id;

      if (!messageId) return;

      if (messageId === streamingMessageID) {
        setLastUpdateMessageTime(Date.now());
      }

      // Use a single state update to prevent race conditions and duplicates
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        let hasChanges = false;

        // Find message index once
        const messageIndex = updatedMessages.findIndex(
          (msg) => msg._id === messageId
        );

        if (messageIndex === -1) {
          // Message not found in current state - skip processing to prevent duplicates
          return prevMessages;
        }

        const currentMessage = updatedMessages[messageIndex];
        const newMessage = { ...currentMessage };

        // Handle callback metadata updates
        if (
          mainMessageObj.status === "fulfilled" &&
          (mainMessageObj?.callbackMetadata?._type === "suggestion" ||
            mainMessageObj?.callbackMetadata?._type === "nextQuestion")
        ) {
          newMessage.callbackMetadata = mainMessageObj.callbackMetadata;
          hasChanges = true;
        }

        // Handle metadata updates
        if (
          (mainMessageObj.status === "fulfilled" ||
            mainMessageObj?.role !== "assistant") &&
          mainMessageObj?.metadata
        ) {
          newMessage.text = removeSpecificUnicodeAtEnd(mainMessageObj?.text);
          newMessage.metadata =
            typeof mainMessageObj.metadata === "string"
              ? JSON.parse(mainMessageObj.metadata)
              : mainMessageObj.metadata;
          hasChanges = true;
        }

        // Handle fulfilled AI streaming messages
        if (mainMessageObj.status === "fulfilled" && isAIStreaming) {
          if (messageId === _streamingMessageID) {
            if (
              primaryAgentModeRef.current !== "planner" &&
              primaryAgentModeRef.current !== "computer"
            ) {
              setIsAIStreaming(false);
            }

            if (
              mainMessageObj.text !== "**...**" &&
              mainMessageObj.text !== processedText &&
              primaryAgent?.autoVoiceMode
            ) {
              updateTextForSpeech(
                `${message}${mainMessageObj.status === "fulfilled" ? "." : ""}`,
                mainMessageObj?.chatID,
                messageId
              );
            }

            // Update chat state
            chatsHelper.updateChat(selectedNode?.id, [
              {
                property: "isAIReplying",
                value: primaryAgentModeRef.current === "planner",
              },
              {
                property: "activeMessageForAI",
                value: null,
              },
            ]);
            setStreamingMessageID(null);
          }

          // Update message content for streaming messages
          if (messageIndex !== -1) {
            newMessage.text =
              messageId === streamingMessageID ||
              primaryAgentModeRef.current === "planner"
                ? removeSpecificUnicodeAtEnd(message)
                : currentMessage.text;
            newMessage.images = mainMessageObj?.images;
            newMessage.audios = mainMessageObj?.audios;
            newMessage.videos = mainMessageObj?.videos;
            newMessage.files = mainMessageObj?.files;
            newMessage.loading = false;
            newMessage.metadata =
              typeof mainMessageObj.metadata === "string"
                ? JSON.parse(mainMessageObj.metadata)
                : mainMessageObj.metadata;
            newMessage.callbackMetadata =
              typeof mainMessageObj.callbackMetadata === "string"
                ? JSON.parse(mainMessageObj.callbackMetadata)
                : mainMessageObj.callbackMetadata;
            newMessage.agentName = getAgent(mainMessageObj?.agentID)?.label;
            hasChanges = true;
          }
        }
        // Handle metadata-only updates for AI messages
        else if (
          mainMessageObj.metadata &&
          mainMessageObj.text === "**...**" &&
          mainMessageObj.isAI === true &&
          (messageId === _streamingMessageID ||
            primaryAgentModeRef.current === "planner")
        ) {
          const _meta =
            typeof mainMessageObj.metadata === "string"
              ? JSON.parse(mainMessageObj.metadata)
              : mainMessageObj.metadata;

          const currentText = currentMessage?.text || "";
          const cleanText = removeSpecificUnicodeAtEnd(currentText);
          newMessage.text = `${cleanText}${
            isAIStreamingRef.current ? " " + cursor : ""
          }`;
          newMessage.metadata = _meta;
          newMessage.agentName = getAgent(mainMessageObj?.agentID)?.label;
          hasChanges = true;
        }
        // Handle regular AI message updates
        else if (
          ((mainMessageObj.text !== "**...**" &&
            mainMessageObj.isAI === true) ||
            (loadingMessageIdRef?.current == null && isAIStreaming) ||
            primaryAgentModeRef.current === "planner") &&
          (messageId === streamingMessageID ||
            primaryAgentModeRef.current === "planner") &&
          message !== "**...**"
        ) {
          setAllowStop(loadingMessageIdRef?.current === messageId);

          const cleanMessage = removeSpecificUnicodeAtEnd(message);
          newMessage.text = `${cleanMessage}${
            isAIStreamingRef.current ? " " + cursor : ""
          }`;
          newMessage.images = mainMessageObj?.images;
          newMessage.audios = mainMessageObj?.audios;
          newMessage.videos = mainMessageObj?.videos;
          newMessage.files = mainMessageObj?.files;
          newMessage.loading = false;
          newMessage.humanFeedback = mainMessageObj?.humanFeedback;
          newMessage.humanFeedbackType = mainMessageObj?.humanFeedbackType;
          newMessage.humanFeedbackSubType =
            mainMessageObj?.humanFeedbackSubType;
          newMessage.humanFeedbackComment =
            mainMessageObj?.humanFeedbackComment;
          newMessage.agentName = getAgent(mainMessageObj?.agentID)?.label;
          newMessage.metadata =
            typeof mainMessageObj.metadata === "string"
              ? JSON.parse(mainMessageObj.metadata)
              : mainMessageObj.metadata;
          newMessage.callbackMetadata =
            typeof mainMessageObj.callbackMetadata === "string"
              ? JSON.parse(mainMessageObj.callbackMetadata)
              : mainMessageObj.callbackMetadata;
          hasChanges = true;
        }

        // Only update if there are actual changes
        if (hasChanges) {
          updatedMessages[messageIndex] = newMessage;
          return updatedMessages;
        }

        return prevMessages;
      });
    };
    return setupSubscription();
  };
  useEffect(() => {
    let _subscriptionObj;
    let _chatSubscriptionObj;
    const setupSubscriptions = async () => {
      _chatSubscriptionObj = await subscribeToPlanner(
        chatId,
        streamingMessageIDRef?.current
      );
      _subscriptionObj = await subscribeToChatMessages(
        chatId,
        streamingMessageIDRef?.current,
        primaryAgent
      );
      setChatSubscriptionObject(_chatSubscriptionObj);
      setSubscriptionObject(_subscriptionObj);
    };
    if (chatId) {
      if (isConnectedToCloud()) setErrorWithSubscription(false);
      if (lastSubscribedChatId == chatId) {
        if (_subscriptionObj) _subscriptionObj?.unsubscribe();
        if (_chatSubscriptionObj) _chatSubscriptionObj?.unsubscribe();
      }
      setLastSubscribedChatId(chatId);
      setAllowStop(false);
      setupSubscriptions();
    }

    return () => {
      if (_subscriptionObj) {
        _subscriptionObj?.unsubscribe();
      }

      if (_chatSubscriptionObj) {
        _chatSubscriptionObj?.unsubscribe();
      }
    };
  }, [
    chatId,
    lastSubscribedChatId,
    getAgent(chatObject?.primaryRole)?.id,
    isAIStreaming,
    streamingMessageIDRef?.current,
    primaryAgent,
  ]);
  useEffect(() => {
    if (!isConnectedToCloud()) {
      setErrorWithSubscription(true);
    }
  }, [isConnectedToCloud()]);
  useEffect(() => {
    if (transcript?.text) {
      AppQuantumEditorUtils.Utilities.insertTextAtSelection(
        editor,
        transcript.text || ""
      );
      setValue(editor?.children);
    }
  }, [transcript]);
  const saveTopics = async (chatId, payload) => {
    try {
      await chatsHelper.updateChat(chatId, payload);
    } catch (error) {
      console.log(error);
      AppToasty.show(t("errorSavingTopics"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  useEffect(() => {
    if (chatId && selectedTopics && isLoading === false) {
      saveTopics(chatId, [
        {
          property: "topics",
          value: selectedTopics ? JSON.stringify(selectedTopics) : null,
        },
      ]);
    }
  }, [selectedTopics?.length]);
  useEffect(() => {
    if (isMobileScreen()) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isMobileScreen()]);
  const adaptMessages = (_messages) => {
    return _messages.map((message) => {
      return {
        _id: message.id,
        user: {
          id: message.userID,
          name: message.role,
          agentName:
            message?.agentID != primaryAgent?.id
              ? getAgent(message?.agentID)?.label
              : null,
        },
        text: message.text,
        createdAt: message.creationTime,
        _lastChangedAt: message?._lastChangedAt,
        metadata: message.metadata,
        callbackMetadata: message.callbackMetadata,
        humanFeedback: message.humanFeedback,
        humanFeedbackType: message.humanFeedbackType,
        humanFeedbackSubType: message.humanFeedbackSubType,
        humanFeedbackComment: message.humanFeedbackComment,
        humanFeedbackForInstruction: message.humanFeedbackForInstruction,
        images: message.images,
        audios: message.audios,
        videos: message.videos,
        files: message.files,
        agentName: getAgent(message?.agentID)?.label,
      };
    });
  };
  const loadMessages = async (chatId) => {
    setIsLoading(true);
    let _messages = await messagesHelper.getChatMessages(chatId);

    _messages = _messages?.data?.messagesByChat?.items?.filter(
      (u) => u?._deleted != true
    );
    _messages = _messages.map((message) => {
      if (typeof message.metadata === "string") {
        message.metadata = JSON.parse(message.metadata);
      }
      if (typeof message.callbackMetadata === "string") {
        message.callbackMetadata = JSON.parse(message.callbackMetadata);
      }
      return message;
    });
    const sortedMessage = _messages.sort((a, b) => {
      return a.creationTime - b.creationTime;
    });
    setStateMessages(adaptMessages(sortedMessage));
    // sort messages by createdAt
    const lastSortedMessages = sortedMessage.slice(-loadedMessages);
    setMessages(adaptMessages(lastSortedMessages));
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  };
  useEffect(() => {
    if (
      chatId &&
      getActiveWorkspace()?.id &&
      chatId !== loadedChatId &&
      state?.agents?.length
    ) {
      setLoadedMessages(MAX_MESSAGES_LOADED);
      setSelectedNode({ id: chatId });
      setLoadedChatId(chatId);
      loadMessages(chatId);
    }
  }, [chatId, getActiveWorkspace()?.id, loadedChatId, state?.agents]);
  const setChatProperties = (chatObj) => {
    if (chatObj?.topics && typeof chatObj?.topics === "string")
      chatObj.topics = JSON.parse(chatObj.topics);
    if (chatObj?.metadata && typeof chatObj?.metadata === "string")
      chatObj.metadata = JSON.parse(chatObj.metadata);
    setIsAIStreaming(chatObj?.isAIReplying == true);
    setActiveStep(chatObj?.metadata?.active_step);
    setPlanningExecution(chatObj?.metadata?.planning_status);
    setTotalSteps(chatObj?.metadata?.plan?.length || 0);
  };
  const getChatObject = async (chatId) => {
    let chatObj = getChat(chatId);
    if (!chatObj && chatId)
      try {
        let result;
        if (isConnectedToCloud()) result = await chatsHelper.getChat(chatId);
        else {
          result = await DataStore.query(Chat, chatId);
          result = {
            data: {
              getChat: {
                ...result,
              },
            },
          };
        }
        chatObj = result?.data?.getChat;
        setChatProperties(chatObj);
        dispatch("SET_CHATS", [...state?.chats, chatObj]);
      } catch (error) {
        console.log(error);
        AppToasty.show(t("errorLoadingChat"), {
          placement: "bottom",
          type: "danger",
        });
      }
    else if (chatObj) {
      setChatProperties(chatObj);
    }
    setChatObject(chatObj);
  };
  const lastMessageFromAssistant =
    messages?.filter((u) => u?.user?.name === "assistant")?.slice(-1)[0] || {};
  useEffect(() => {
    if (chatId != loadedChatId || (!chatObject && chatId)) {
      getChatObject(chatId);
    }
  }, [chatId, getActiveWorkspace()?.id, JSON.stringify(chats), isLoading]);
  useEffect(() => {
    if (chatObject?.primaryRole) {
      const agent = getAgent(chatObject?.primaryRole);
      setCanInteractWithChat(
        chatObject?.visibility !== "widget" || chatObject?.handedOffToHuman
      );
      setPrimaryAgent(agent);
      if (chatObject?.topics && typeof chatObject?.topics === "string") {
        setSelectedTopics(JSON.parse(chatObject?.topics) || []);
      } else {
        setSelectedTopics(chatObject?.topics || []);
      }
      if (chatObject?.metadata && typeof chatObject?.metadata === "string") {
        setChatMetadata(JSON.parse(chatObject?.metadata) || {});
      } else {
        setChatMetadata(chatObject?.metadata || {});
      }
    }
  }, [JSON.stringify(chatObject), JSON.stringify(state?.agents)]);
  useEffect(() => {
    setActiveStep(chatObject?.metadata?.active_step || 0);
    setPlanningExecution(chatObject?.metadata?.planning_status);
    setTotalSteps(chatObject?.metadata?.plan?.length || 0);
  }, [JSON.stringify(chatObject?.metadata)]);
  const goToChat = (chatId) => {
    if (chatId) {
      history.push(`/workspaces/${getActiveWorkspace()?.id}/chats/${chatId}`);
    }
  };
  const goToChatsOverview = () => {
    history.push(`/workspaces/${getActiveWorkspace()?.id}/chats`);
  };
  const removeNode = async (node) => {
    setDeletionInProgress(true);
    setShowModal(false);
    setIsLoading(true);
    if (selectedNode?.id === selectedNodeToDelete.id) {
      try {
        result = await chatsHelper.deleteChat(selectedNode?.id);
        const indexOfDeletedNode =
          state?.treeViewData
            ?.filter((u) => !u.data?.droppable)
            .map((u) => u.id)
            ?.indexOf(selectedNodeToDelete.id) - 1;
        AppToasty.show(t("chatDeleted"), {
          placement: "bottom",
          type: "success",
        });
        setChats(chats.filter((u) => u.id?.toString() != node?.id));
      } catch (error) {
        console.error(error);
        AppToasty.show(t("errorDeletingChat"), {
          placement: "bottom",
          type: "danger",
        });
      } finally {
        goToChatsOverview();
        setDeletionInProgress(false);
        setIsLoading(false);
      }
    }
  };

  const handleClickOnNode = (node, toDelete = false) => {
    if (toDelete) setSelectedNodeToDelete(node);
    else {
      setSelectedNode(node);
      if (
        node &&
        node?.id !== selectedNode?.id &&
        node?.data?.droppable != true
      ) {
        setIsLoading(true);
        goToChat(node?.id);
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }
    }
  };

  const setDialogActionOnTree = useCallback(
    (action) => {
      setShowModal(true);
      setTreeViewActionContext(action);
    },
    [setShowModal, setTreeViewActionContext]
  );
  const onMove = (object) => {
    console.log("onMove", object);
  };
  const executePostChatCreationActions = () => {
    setIsLoading(false);
    setCreationOfNewChatInProgress(false);
    setSelectedTopics([]);
    loadChats(false, true);
    setShowWidgetChatsHistory(false);
  };
  const params = useParams();
  useEffect(() => {
    if (
      params?.chatId &&
      params?.chatId?.includes("newchat") &&
      getActiveWorkspace()?.id &&
      user?.id
    ) {
      const _agent_id = params?.chatId?.split("_")[1];
      handleParentActionClick(_agent_id);
    }
  }, [
    params?.chatId,
    state?.agents?.length,
    getActiveWorkspace()?.id,
    user?.id,
  ]);
  const handleParentActionClick = async (agentid) => {
    if (isConnectedToCloud())
      result = await chatsHelper.createChat({
        workspaceID: getActiveWorkspace()?.id,
        allowMultipleRoles: false,
        createdBy: user?.id,
        visibility: "private",
        primaryRole: agentid,
        plannerAgent: agentid,
        name: "New Chat",
        creationTime: Math.floor(new Date().getTime() / 1000),
      });
    else {
      result = await DataStore.save(
        new Chat({
          workspaceID: getActiveWorkspace()?.id,
          allowMultipleRoles: false,
          createdBy: user?.id,
          visibility: "private",
          primaryRole: agentid,
          plannerAgent: agentid,
          name: "New Local Chat",
          creationTime: Math.floor(new Date().getTime() / 1000),
        })
      );
      result = {
        data: {
          createChat: result,
        },
      };
    }
    history.push(
      `/workspaces/${getActiveWorkspace()?.id}/chats/${
        result?.data?.createChat?.id
      }`
    );
    AppToasty.show(t("chatCreationCompleted"), {
      placement: "bottom",
      type: "success",
    });
    setIsCollapsed(true);
    executePostChatCreationActions();
  };
  const createChat = async (selectedNode, values, isEdit) => {
    try {
      setIsLoading(true);
      setCreationOfNewChatInProgress(true);
      let channelSettings = null;
      let externalParticipantId = null;
      if (values?.toPhoneNumber || values?.toEmail) {
        if (values?.channelType == "email")
          externalParticipantId = values?.toEmail;
        else if (
          values?.channelType == "sms" ||
          values?.channelType == "whatsapp"
        )
          externalParticipantId = values?.toPhoneNumber;
        channelSettings = {
          sms: {
            isEnabled: values?.channelType == "sms",
            recipient: values?.toPhoneNumber,
            providerID:
              values?.channelType == "sms" ? values?.providerId : null,
          },
          whatsapp: {
            isEnabled: values?.channelType == "whatsapp",
            recipient: values?.toPhoneNumber,
            providerID:
              values?.channelType == "whatsapp" ? values?.providerId : null,
          },
          email: {
            isEnabled: values?.channelType == "email",
            recipient: values?.toEmail,
            providerID:
              values?.channelType == "email" ? values?.providerId : null,
          },
        };
      }
      delete values.toPhoneNumber;
      delete values.toEmail;
      delete values.channelType;
      delete values.providerId;
      // CREATE CHAT
      if (isEdit) {
        result = await chatsHelper.updateChat(selectedNode?.id, [
          { property: "name", value: values.name },
          { property: "description", value: values.description },
          { property: "primaryRole", value: values.primaryRole },
          { property: "plannerAgent", value: values.primaryRole },
          {
            property: "visibility",
            value: values.isPublic ? "public" : "private",
          },
          {
            property: "channelSettings",
            value: JSON.stringify(channelSettings),
          },
          {
            property: "externalParticipantId",
            value: externalParticipantId,
          },
        ]);

        AppToasty.show(t("chatUpdateCompleted"), {
          placement: "bottom",
          type: "success",
        });
      } else {
        const _visibility = values.isPublic ? "public" : "private";
        delete values.isPublic;
        result = await chatsHelper.createChat({
          ...values,
          plannerAgent: values.primaryRole,
          workspaceID: getActiveWorkspace()?.id,
          allowMultipleRoles: false,
          createdBy: user?.id,
          visibility: _visibility,
          creationTime: Math.floor(new Date().getTime() / 1000),
          channelSettings: JSON.stringify(channelSettings),
          externalParticipantId: externalParticipantId,
        });
        history.push(
          `/workspaces/${getActiveWorkspace()?.id}/chats/${
            result?.data?.createChat?.id
          }`
        );
        AppToasty.show(t("chatCreationCompleted"), {
          placement: "bottom",
          type: "success",
        });
      }
      setShowModal(false);
      setIsLoading(false);
      setCreationOfNewChatInProgress(false);
      setSelectedTopics([]);
      loadChats(false, true);
      setShowWidgetChatsHistory(false);
      setIsCollapsed(true);
    } catch (error) {
      console.error("Error creating chat", error);
      AppToasty.show(t("chatCreationError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const updatePlanApprovalState = async (id) => {
    try {
      // console.log("Updating plan approval state for ID:", id);
      let metadata = messages?.find((msg) => msg.id === id)?.metadata;
      if (typeof metadata == "string") metadata = JSON.parse(metadata);
      await messagesHelper.updateChatMessage(id, [
        {
          property: "metadata",
          value: JSON.stringify({
            ...metadata,
            quickApprove: false,
          }),
        },
      ]);
      const indexMessage = messages
        ?.map((u) => u.id)
        ?.indexOf((msg) => msg === id);
      if (indexMessage > -1) {
        let _messages = [...messages];
        _messages[indexMessage].metadata = metadata;
        setMessages(_messages);
      }
      setPlanningExecution("planning_approved");
    } catch (error) {
      console.error("Error updating chat message", error);
      AppToasty.show(t("chatApprovalError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const onLoadEarlier = async () => {
    let newLoadedMessages = loadedMessages + MAX_MESSAGES_LOADED;
    setMessages(stateMessages.slice(-newLoadedMessages));
    setLoadedMessages(newLoadedMessages);
  };
  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function startStreaming(message, primaryAgent, assistantMessageId) {
    const controller = new AbortController();
    const signal = controller.signal;
    let existingMessages = [];
    if (messagesRef?.current?.length > primaryAgent?.maxHistory || 10)
      existingMessages = messagesRef?.current.slice(-primaryAgent?.maxHistory);
    else existingMessages = messagesRef?.current || [];

    existingMessages = existingMessages?.map((u) => {
      return {
        role: u?.user?.id == null ? "assistant" : "user",
        content: u?.text,
      };
    });

    fetch(`${agentHosting?.endpoint}/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        model: primaryAgent?.llmBaseModel,
        messages: [
          { role: "system", content: generateSystemMessage(primaryAgent) },
          ...existingMessages,
          {
            role: "user",
            content: message,
          },
        ],
        options: {
          temperature: primaryAgent?.temperature || 0.7,
          num_ctx: primaryAgent?.maxTokens || 8192,
          stop: ["</s>"],
        },
        // format: "json",
      }),
      signal: signal,
    })
      .then((response) => {
        handleStreamResponse(response, controller, assistantMessageId);
      })
      .catch(handleStreamError);
    return controller; // Return the controller for external abort access
  }
  function handleStreamError(err) {
    if (err.name === "AbortError") {
      console.log("Fetch aborted");
    } else {
      console.error("Fetch error:", err);
    }
    setIsAIStreaming(false);
    setAllowStop(false);
  }
  const getMessages = () => {
    return messagesRef.current;
  };
  const updateMessagesWhileStreamingOffline = async (
    text,
    assistantMessageId,
    getMessages
  ) => {
    const assistaMessageIndex = getMessages()
      ?.map((u) => u?._id)
      ?.indexOf(assistantMessageId);
    let newMessages = [...getMessages()];
    newMessages[assistaMessageIndex] = {
      ...newMessages[assistaMessageIndex],
      text,
      loading: false,
    };
    setMessages(newMessages);
  };
  function parseJSONSafely(str) {
    try {
      // First, try parsing the string as-is
      return JSON.parse(str);
    } catch (e) {
      // If that fails, try to escape special characters
      try {
        const escaped = str.replace(/[\n\r\t]/g, (match) => {
          const escapeMap = {
            "\n": "\\n",
            "\r": "\\r",
            "\t": "\\t",
          };
          return escapeMap[match] || match;
        });
        return JSON.parse(escaped);
      } catch (e2) {
        // If all else fails, throw an error
        throw new Error("Unable to parse JSON: " + e2.message);
      }
    }
  }
  function handleStreamResponse(response, controller, assistantMessageId) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";
    function processStream(assistantMessageId) {
      reader
        .read()
        .then(async ({ done, value }) => {
          if (done) {
            await messagesHelper.updateChatMessage(assistantMessageId, [
              {
                property: "text",
                value: fullText,
              },
            ]);
            setIsAIStreaming(false);
            setAllowStop(true);
            return;
          }

          const chunk = decoder.decode(value);
          try {
            // console.log("Chunk", parseJSONSafely(chunk)?.message?.content);
            fullText += parseJSONSafely(chunk)?.message?.content;
          } catch (ex) {
            const _jsonArray = chunk?.split("\n");
            if (_jsonArray?.length > 0) {
              _jsonArray?.map((u) => {
                try {
                  const _json_chunk = JSON.parse(u);
                  fullText += _json_chunk?.message?.content;
                } catch (error) {}
              });
            }
          }
          updateMessagesWhileStreamingOffline(
            fullText,
            assistantMessageId,
            getMessages
          );

          processStream(assistantMessageId); // Continue reading
        })
        .catch(async (err) => {
          if (err.name === "AbortError") {
            console.log("Stream aborted");
          } else {
            console.error("Stream error:", err);
          }
          await messagesHelper.updateChatMessage(assistantMessageId, [
            {
              property: "text",
              value: fullText,
            },
          ]);
        });
    }
    processStream(assistantMessageId);
  }

  const onSendEvent = async (message) => {
    try {
      if (isSendingEventRef?.current == true) return;
      if (message.trim().length === 0) return;
      if (agentHosting?.type == "ollama" && !isOllamaRunning()) {
        AppToasty.show(t("ollamaNotRunning"), {
          placement: "bottom",
          type: "danger",
        });
        return;
      }
      isSendingEventRef.current = true;
      if (agentHosting?.type == "ollama") {
        setAllowStop(true);
        setIsAIStreaming(true);
        const promises = [
          messagesHelper.createChatMessage({
            chatID: selectedNode?.id,
            workspaceID: getActiveWorkspace()?.id,
            text: message,
            role: user?.username,
            userID: user?.id,
            isAI: false,
            creationTime: Math.floor(new Date().getTime() / 1000),
            images: imageKey ? [imageKey] : null,
            audios: audioKey ? [audioKey] : null,
            videos: videoKey ? [videoKey] : null,
            files: fileKeys ? fileKeys : null,
            // audiosText: audiosText,
            // videosText: videosText,
          }),
          messagesHelper.createChatMessage({
            chatID: selectedNode?.id,
            workspaceID: getActiveWorkspace()?.id,
            text: "**...**",
            role: "assistant",
            isAI: true,
            creationTime: Math.floor(new Date().getTime() / 1000) + 1,
            agentID: primaryAgent?.id,
          }),
        ];
        const results = await Promise.all(promises);
        let newMessage = results[0]?.data?.createChatMessage;
        let assistantPlaceholder = results[1]?.data?.createChatMessage;
        setMessages((previousMessages) =>
          generateUniqueMessages([
            ...previousMessages,
            {
              _id: newMessage?.id,
              text: message,
              createdAt: Math.floor(new Date().getTime() / 1000),
              user: { id: user?.id, name: user?.username, agentName: null },
              loading: false,
              images: imageKey ? [imageKey] : null,
              audios: audioKey ? [audioKey] : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
            },
            {
              _id: assistantPlaceholder?.id,
              text: "",
              createdAt: Math.floor(new Date().getTime() / 1000) + 1,
              user: {
                id: null,
                name: "assistant",
                parentMessageId: newMessage?.id,
                agentName: primaryAgent?.label,
              },
              loading: true,
            },
          ])
        );
        setStreamingMessageID(assistantPlaceholder?.id);
        localStreamingController.current = startStreaming(
          message,
          primaryAgent,
          assistantPlaceholder?.id
        );
        return;
      }
      let promises = [];
      if (
        (chatObject?.isAIReplying != true || isAIStreaming) &&
        activeStep > 0 &&
        totalSteps > 0 &&
        primaryAgent?.mode == "planner"
      ) {
        console.log(
          "Planner mode, sending prompt to model...",
          chatIdRef.current,
          chatId,
          selectedNode?.id,
          getActiveWorkspace()?.id
        );
        promises = [
          messagesHelper.createChatMessage({
            chatID: chatIdRef.current,
            workspaceID: getActiveWorkspace()?.id,
            text: message,
            role: user?.username,
            userID: user?.id,
            isAI: false,
            creationTime: Math.floor(new Date().getTime() / 1000),
            images: imageKey ? [imageKey] : null,
            audios: audioKey ? [audioKey] : null,
            videos: videoKey ? [videoKey] : null,
            files: fileKeys ? fileKeys : null,
            // audiosText: audiosText,
            // videosText: videosText,
          }),
        ];
        const results = await Promise.all(promises);
        let newMessage = results[0]?.data?.createChatMessage;
        // console.log(newMessage, "new message");
        setMessages((previousMessages) =>
          generateUniqueMessages([
            ...previousMessages,
            {
              _id: newMessage?.id,
              text: message,
              createdAt: Math.floor(new Date().getTime() / 1000) + 3,
              _lastChangedAt: newMessage?._lastChangedAt,
              user: { id: user?.id, name: user?.username, agentName: null },
              loading: false,
              images: imageKey ? [imageKey] : null,
              audios: audioKey ? [audioKey] : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
            },
          ])
        );
        const _requestsResults = await Promise.all([
          Chatter.getAnswerinChat({
            chatid: chatIdRef.current,
            messages: [
              {
                text: message,
                role: user?.username,
                userID: user?.id,
              },
            ],
            baseImage: selectedImageRawKey,
            agentid: primaryAgent?.id,
            token: getActiveWorkspace()?.workspaceToken,
            workspaceid: getActiveWorkspace()?.id,
            stream: "true",
            nextMessageId: `feedback_${newMessage?.id}`,
            methodName: "planner",
          }),
        ]);
        if (_requestsResults[0]?.data?.errorCode == 401) {
          AppToasty.show(_requestsResults[0]?.data?.errorDetail, {
            placement: "bottom",
            type: "danger",
          });
        }
      } else {
        if (isAIStreaming && streamingMessageID) {
          subscriptionObj?.unsubscribe();
          await messagesHelper.updateChatMessage(streamingMessageID, [
            {
              property: "status",
              value: "cancelled",
            },
          ]);
          setIsAIStreaming(false);
          setLastUpdateMessageTime(null);
          await wait(1000);
        } else if (isSubscriptionRequired)
          setSubscriptionObject(
            subscribeToChatMessages(chatId, streamingMessageID)
          );
        if (chatObject?.handedOffToHuman) {
          promises = [
            messagesHelper.createChatMessage({
              chatID: chatIdRef.current,
              workspaceID: getActiveWorkspace()?.id,
              text: message,
              role: "assistant",
              userID: user?.id,
              isAI: false,
              creationTime: Math.floor(new Date().getTime() / 1000),
              images: imageKey ? [imageKey] : null,
              audios: audioKey ? [audioKey] : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
              // audiosText: audiosText,
              // videosText: videosText,
            }),
            chatsHelper.updateChat(chatId, [
              {
                property: "isAIReplying",
                value: false,
              },
              {
                property: "forcePlannerStop",
                value: false,
              },
            ]),
          ];
          const results = await Promise.all(promises);
          let newMessage = results[0]?.data?.createChatMessage;
          setMessages((previousMessages) =>
            generateUniqueMessages([
              ...previousMessages,
              {
                _id: newMessage?.id,
                text: message,
                createdAt: Math.floor(new Date().getTime() / 1000),
                _lastChangedAt: newMessage?._lastChangedAt,
                user: {
                  id: user?.id,
                  name: "assistant",
                  agentName: null,
                  isUser: true,
                },
                loading: false,
                images: imageKey ? [imageKey] : null,
                audios: audioKey ? [audioKey] : null,
                videos: videoKey ? [videoKey] : null,
                files: fileKeys ? fileKeys : null,
              },
            ])
          );
          isSendingEventRef.current = false;
        } else {
          console.log(
            "Planner mode, sending prompt to model...",
            chatIdRef.current,
            primaryAgent?.id,
            user?.id
          );
          promises = [
            messagesHelper.createChatMessage({
              chatID: chatIdRef.current,
              workspaceID: getActiveWorkspace()?.id,
              text: message,
              role: user?.username,
              userID: user?.id,
              isAI: false,
              creationTime: Math.floor(new Date().getTime() / 1000),
              images: imageKey ? [imageKey] : null,
              audios: audioKey ? [audioKey] : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
              // audiosText: audiosText,
              // videosText: videosText,
            }),
            messagesHelper.createChatMessage({
              chatID: chatIdRef.current,
              workspaceID: getActiveWorkspace()?.id,
              text: "**...**",
              role: "assistant",
              isAI: true,
              creationTime: Math.floor(new Date().getTime() / 1000) + 1,
              agentID: primaryAgent?.id,
            }),
            chatsHelper.updateChat(chatId, [
              {
                property: "isAIReplying",
                value: true,
              },
              {
                property: "forcePlannerStop",
                value: false,
              },
            ]),
          ];
          const results = await Promise.all(promises);
          setAllowStop(false);
          setIsAIStreaming(true);

          let newMessage = results[0]?.data?.createChatMessage;
          let assistantPlaceholder = results[1]?.data?.createChatMessage;
          setMessages((previousMessages) =>
            generateUniqueMessages([
              ...previousMessages,
              {
                _id: newMessage?.id,
                text: message,
                createdAt: Math.floor(new Date().getTime() / 1000),
                _lastChangedAt: newMessage?._lastChangedAt,
                user: { id: user?.id, name: user?.username, agentName: null },
                loading: false,
                images: imageKey ? [imageKey] : null,
                audios: audioKey ? [audioKey] : null,
                videos: videoKey ? [videoKey] : null,
                files: fileKeys ? fileKeys : null,
              },
              {
                _id: assistantPlaceholder?.id,
                text: "",
                createdAt: Math.floor(new Date().getTime() / 1000) + 1,
                _lastChangedAt: assistantPlaceholder?._lastChangedAt + 1,
                user: {
                  id: null,
                  name: "assistant",
                  parentMessageId: newMessage?.id,
                  agentName: primaryAgent?.label,
                },
                loading: true,
              },
            ])
          );
          setStreamingMessageID(assistantPlaceholder?.id);
          isSendingEventRef.current = false;
          const _requestsResults = await Promise.all([
            Chatter.getAnswerinChat({
              chatid: chatIdRef.current,
              messages: [
                {
                  text: message,
                  role: user?.username,
                  userID: user?.id,
                },
              ],
              baseImage: selectedImageRawKey,
              agentid: primaryAgent?.id,
              token: getActiveWorkspace()?.workspaceToken,
              workspaceid: getActiveWorkspace()?.id,
              stream: "true",
              nextMessageId: assistantPlaceholder?.id,
              methodName:
                primaryAgent?.mode == "planner" ? "planner" : "chatter",
            }),
            chatsHelper.updateChat(chatIdRef.current, [
              {
                property: "isAIReplying",
                value: true,
              },
              {
                property: "activeMessageForAI",
                value: assistantPlaceholder?.id,
              },
              {
                property: "forcePlannerStop",
                value: false,
              },
            ]),
          ]);
          if (_requestsResults[0]?.data?.errorCode == 401) {
            AppToasty.show(_requestsResults[0]?.data?.errorDetail, {
              placement: "bottom",
              type: "danger",
            });
          }
        }
      }
      // Change this
    } catch (error) {
      console.error(error);
      try {
        setIsAIStreaming(false);
        await chatsHelper.updateChat(chatIdRef.current, [
          {
            property: "isAIReplying",
            value: false,
          },
          {
            property: "activeMessageForAI",
            value: null,
          },
          {
            property: "forcePlannerStop",
            value: false,
          },
        ]);
      } catch (error) {
        console.error(error);
      }

      AppToasty.show(t("chatMessageAdditionError"), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      // remove the loading message by checking the loading property
      // setMessages((previousMessages) =>
      //   previousMessages.filter((m) => !m.loading)
      // );
    }
  };
  const onSelectedTopicsChange = useCallback(
    (selectedItems) => {
      if (typeof selectedItems === "string") {
        selectedItems = JSON.parse(selectedItems);
      }
      if (!isLoading) setSelectedTopics(selectedItems);
    },
    [setSelectedTopics, isLoading]
  );
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadChats();
    }
  }, [getActiveWorkspace()?.id]);
  useEffect(() => {
    window.onmessage = (e) => {
      if (e?.data?.tf_event_type == "form_submit" && "data" in e.data) {
        alert(JSON.stringify(e.data));
      }
    };
  }, [chatId]);
  useEffect(() => {
    // make sure chats are unique by id
    let uniqueChats = [];
    if (chats) {
      uniqueChats = chats.filter(
        (u, index, self) => index === self.findIndex((t) => t?.id === u?.id)
      );
    }
    setCoreTreeViewData(groupChatsByProperty(uniqueChats, "createdAt"));
  }, [
    JSON.stringify(
      chats?.map((u) => {
        return {
          id: u?.id,
          createdAt: u?.createdAt,
          primaryRole: u?.primaryRole,
          visibility: u?.visibility,
          name: u?.name,
          description: u?.description,
        };
      })
    ),
    state?.agents?.length,
  ]);
  const onDrop = async ({
    newTreeData,
    dragSourceId,
    dropTargetId,
    rest,
  }) => {};
  const handleShowSourcesDetails = (sources) => {
    setSelectedSources(sources);
    setShowSourcesModal(true);
  };
  function groupChatsByProperty(chats, property = "primaryRole") {
    let groupedChats = {};
    state?.agents
      ?.filter((u) => u?.agentType == "Chatbot")
      ?.map((u) => {
        groupedChats[u.id] = {
          id: u.id,
          name: u.label,
          description: u?.description,
          is_agent: true,
          agentType: u?.agentType,
          icon: u?.icon,
          color: u?.color,
          darkColor: u?.darkColor,
          mode: u?.mode,
          droppable: true,
          children: [],
        };
      });

    chats
      .filter((chat) => chat != null)
      ?.forEach((chat) => {
        const groupKey = chat[property];
        const agent = state?.agents?.find(
          (agent) => agent.id === chat["primaryRole"]
        );
        const date = dayjs(groupKey);
        const formattedDate = date.format("DD MMM YY");
        if (!agent) {
          if (!groupedChats["deleted_agents_id"])
            groupedChats["deleted_agents_id"] = {
              id: "deleted_agents_id",
              name: "Agent not available",
              description: "Description not available",
              is_agent: true,
              is_agent_available: false,
              droppable: true,
              children: [],
            };
          if (
            !groupedChats["deleted_agents_id"].children.find(
              (u) => u.id === "deleted_agents_id" + formattedDate
            )
          ) {
            groupedChats["deleted_agents_id"].children.push({
              id: "deleted_agents_id" + formattedDate,
              name: formattedDate, // Make sure the getAgent() function is defined
              droppable: true,
              children: [],
              date: date,
            });
          }

          groupedChats["deleted_agents_id"].children
            .find((u) => u.id === "deleted_agents_id" + formattedDate)
            .children.push(chat);
          return;
        }

        if (!groupedChats[agent?.id])
          groupedChats[agent?.id] = {
            id: agent?.id,
            name: agent?.label,
            description: agent?.description,
            is_agent: true,
            agentType: agent?.agentType,
            mode: agent?.mode,
            droppable: true,
            children: [],
            date: date,
          };
        if (
          !groupedChats[agent?.id].children.find(
            (u) => u.id === agent?.id + formattedDate
          )
        ) {
          groupedChats[agent?.id].children.push({
            id: agent?.id + formattedDate,
            name: formattedDate, // Make sure the getAgent() function is defined
            droppable: true,
            children: [],
            date: date,
          });
          groupedChats[agent?.id].children.sort((a, b) => b.date - a.date);
        }

        groupedChats[agent?.id].children
          .find((u) => u.id === agent?.id + formattedDate)
          .children.push(chat);

        const targetGroup = groupedChats[agent?.id].children.find(
          (u) => u.id === agent?.id + formattedDate
        );

        if (targetGroup && targetGroup.children)
          targetGroup.children.sort((a, b) => {
            // Convert strings to Date objects for comparison
            const dateA = dayjs(a.createdAt);
            const dateB = dayjs(b.createdAt);

            // Sort in descending order (newest first)
            return dateB.valueOf() - dateA.valueOf();
          });
      });
    // Convert the grouped chats to an array and sort by date in descending order
    const sortedGroupedChats = Object.values(groupedChats).sort((a, b) => {
      return b?.date?.valueOf() - a?.date?.valueOf();
    });
    return sortedGroupedChats;
  }
  const isAIReplyingOtherUsers =
    state?.chats?.find((chat) => chat?.id === chatId)?.isAIReplying &&
    !isAIStreaming;
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ChatsScreenView
        cursor={cursor}
        showWidgetChatsHistory={showWidgetChatsHistory}
        handleClickOnWidgetChatsHistory={handleClickOnWidgetChatsHistory}
        setShowWidgetChatsHistory={setShowWidgetChatsHistory}
        isMoreToLoad={!state?.chatsLoaded}
        errorWithSubscription={errorWithSubscription}
        handleClickOnNode={handleClickOnNode}
        SaveChat={createChat}
        selectedNode={selectedNode}
        deleteNode={removeNode}
        showModal={showModal}
        setShowModal={setShowModal}
        setDialogActionOnTree={setDialogActionOnTree}
        treeViewActionContext={treeViewActionContext}
        isTreeDataLoading={!isChatHistoryLoaded || deletionInProgress}
        selectedTopics={selectedTopics}
        onSelectedTopicsChange={onSelectedTopicsChange}
        topics={generateAdaptedTopics(topics)}
        onDrop={onDrop}
        isSocketConnected={isSocketConnected}
        _coreTreeViewData={_coreTreeViewData}
        selectedNodeToDelete={selectedNodeToDelete}
        creationOfNewChatInProgress={creationOfNewChatInProgress}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        setSelectedNode={setSelectedNode}
        onMove={onMove}
        chatId={chatId}
        isLoading={isLoading}
        primaryAgent={primaryAgent}
        messages={messages?.map((u, index) => {
          const isLast = index === messages.length - 1;
          return isLast
            ? u
            : {
                ...u,
                text: removeSpecificUnicodeAtEnd(u?.text),
              };
        })}
        setMessages={setMessages}
        onSendEvent={onSendEvent}
        chatObject={chatObject}
        isAIStreaming={isAIStreaming && chatObject?.forcePlannerStop != true}
        isAIReplyingOtherUsers={isAIReplyingOtherUsers}
        onCancelGeneration={onCancelGeneration}
        inputText={inputText}
        setInputText={setInputText}
        onLoadEarlier={onLoadEarlier}
        allMessagesLoaded={stateMessages.length >= loadedMessages}
        subscriptionObj={subscriptionObj}
        streamingMessageID={streamingMessageID}
        documents={state?.embeddedMrcDocuments}
        documentId={documentId}
        setTreeViewActionContext={setTreeViewActionContext}
        S3downloadUrlRequest={S3downloadUrlRequest}
        handleFeedback={handleFeedback}
        workspaceid={getActiveWorkspace()?.id}
        onImageSelected={onImageSelected}
        selectedImageKey={selectedImageKey}
        handleCopy={handleCopy}
        handleShowSourcesDetails={handleShowSourcesDetails}
        showSourcesModal={showSourcesModal}
        setShowSourcesModal={setShowSourcesModal}
        selectedSources={selectedSources}
        allowStop={allowStop}
        setIsChatHistoryLoaded={setIsChatHistoryLoaded}
        isLastMessageEmpty={lastMessageFromAssistant?.text == ""}
        canInteractWithChat={canInteractWithChat}
        handleParentActionClick={handleParentActionClick}
        onImageUploaded={onImageUploaded}
        imageUrl={imageUrl}
        setImageUrl={setImageUrl}
        onAudioUploaded={onAudioUploaded}
        audioUrl={audioUrl}
        setAudioUrl={setAudioUrl}
        onVideoUploaded={onVideoUploaded}
        videoUrl={videoUrl}
        setVideoUrl={setVideoUrl}
        onFileUploaded={onFileUploaded}
        fileUrl={fileUrl}
        setFileUrl={setFileUrl}
        onFilesUploaded={onFilesUploaded}
        fileUrls={fileUrls}
        setFileUrls={setFileUrls}
        forceAgentsName={getAgent(chatObject?.plannerAgent)?.mode == "planner"}
        activeStep={activeStep}
        isFeedbackOnPlanAllowed={
          (chatObject?.isAIReplying || isAIStreaming) &&
          activeStep > 0 &&
          totalSteps > 0 &&
          primaryAgent?.mode == "planner"
        }
        planningExecution={planningExecution}
        totalSteps={totalSteps}
        isSpeechGettingProcessed={isSpeechGettingProcessed}
        updateTextForSpeech={updateTextForSpeech}
        stopAudio={() => {
          stopAudio();
          setIsPlaying(false);
        }}
        isPlaying={isPlaying}
        listenToAudio={listenToAudio}
        lastUpdateMessageTime={lastUpdateMessageTime}
        agentHosting={agentHosting}
        planArtifacts={planArtifacts}
        updatePlanApprovalState={updatePlanApprovalState}
      />
    </Suspense>
  );
};

export default Chats;
